import authSvc from '@efex-services/auth/authSvc';
import { IWhiteLabel } from '@efex-services/auth/types';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FINKARGO,
  ORGANIZATION_ID,
  ORGANIZATION_LOG_URL,
  PROVIDER_CODE,
  WHITE_LABEL_DATA,
} from '../constants';
import selectWhiteLabelState from '../store/whiteLabel.selector';
import { reset, setWhiteLabel } from '../store/whiteLabel.slice';
import { UseWhiteLabelReturn } from '../types';

const useWhiteLabelService = (): UseWhiteLabelReturn => {
  const whiteLabelState = useSelector(selectWhiteLabelState);
  const dispatch = useDispatch();
  const [isWhiteLabel, setIsWhiteLabel] = useState<boolean | undefined>(
    undefined
  );

  const getWhiteLabelFromLocalStorage = useCallback((): boolean => {
    const organizationId = sessionStorage.getItem(ORGANIZATION_ID);
    const organizationLogoURL = sessionStorage.getItem(ORGANIZATION_LOG_URL);
    const providerCode = sessionStorage.getItem(PROVIDER_CODE);

    if (organizationId || organizationLogoURL || providerCode) {
      dispatch(
        setWhiteLabel({
          [ORGANIZATION_ID]: organizationId || undefined,
          [ORGANIZATION_LOG_URL]: organizationLogoURL || undefined,
          [PROVIDER_CODE]: providerCode || undefined,
        })
      );
    }
    return !!organizationId;
  }, [dispatch]);

  const setWhiteLabelToLocalStorage = useCallback((values: IWhiteLabel) => {
    const { organizationID, organizationLogoURL, providerCode } = values;
    if (organizationID) {
      sessionStorage.setItem(ORGANIZATION_ID, organizationID);
    }
    if (organizationLogoURL) {
      sessionStorage.setItem(ORGANIZATION_LOG_URL, organizationLogoURL);
    }
    if (providerCode) {
      sessionStorage.setItem(PROVIDER_CODE, providerCode);
    }
  }, []);

  const getWhiteLabel = useCallback(
    async (organizationID: string) => {
      try {
        const values = await authSvc.getWhiteLabel({
          organization: organizationID,
        });
        setWhiteLabelToLocalStorage(values);
        dispatch(setWhiteLabel(values));
      } catch (error) {
        setWhiteLabelToLocalStorage(WHITE_LABEL_DATA);
        dispatch(setWhiteLabel(WHITE_LABEL_DATA));
      }
    },
    [dispatch, setWhiteLabelToLocalStorage]
  );

  const resetWhiteLabel = useCallback(() => {
    sessionStorage.removeItem(ORGANIZATION_ID);
    sessionStorage.removeItem(ORGANIZATION_LOG_URL);
    dispatch(reset());
  }, [dispatch]);

  const validateWhiteLabel = useCallback(async () => {
    const organizationSubdomain = window.location.hostname
      .toLowerCase()
      .includes(FINKARGO);

    const isWhiteLabelStorage = getWhiteLabelFromLocalStorage();

    const searchParams = new URLSearchParams(window.location.search);
    const organizationName = searchParams.get(ORGANIZATION_ID);

    if (organizationName) {
      setIsWhiteLabel(true);
      await getWhiteLabel(organizationName);
      const url = new URL(window.location.href);
      url.searchParams.delete(ORGANIZATION_ID);
      window.history.replaceState({}, '', url.toString());
      return;
    }

    if (organizationSubdomain) {
      setIsWhiteLabel(true);
      await getWhiteLabel(FINKARGO);
    } else if (isWhiteLabelStorage) {
      setIsWhiteLabel(isWhiteLabelStorage);
    } else {
      resetWhiteLabel();
      setIsWhiteLabel(false);
    }
  }, [getWhiteLabel, getWhiteLabelFromLocalStorage, resetWhiteLabel]);

  useEffect(() => {
    validateWhiteLabel();
  }, [validateWhiteLabel]);

  return {
    whiteLabelState,
    getWhiteLabel,
    resetWhiteLabel,
    getWhiteLabelFromLocalStorage,
    setWhiteLabelToLocalStorage,
    validateWhiteLabel,
    isWhiteLabel,
  };
};

export default useWhiteLabelService;
